import React, { useEffect, useState, useRef } from 'react';
import { machineService, sessionService, clientService, zoneService, packageService, categoryService } from '../services/api';
import { useSocket } from '../hooks/useSocket';
import { maskDate, isValidDate, dateToIso } from '../utils/masks';

const WARN_SECS = 300;   // 5 min → aviso (âmbar + sonar)
const CRIT_SECS = 60;    // 1 min → crítico (vermelho)
const buildPaymentSplits = (total, cashValue, otherMethod) => {
  const cents = Math.round(Number(total || 0) * 100);
  const cash = Math.max(0, Math.min(cents, Math.round(Number(cashValue || 0) * 100)));
  return [cash && { payment_method: 'cash', amount: cash / 100 }, cents > cash && { payment_method: otherMethod, amount: (cents - cash) / 100 }].filter(Boolean);
};

// segundos restantes da sessão (null = avulso sem tempo definido → conta pra cima)
function remainingSecs(machine, now) {
  if (machine.session_notes === 'UNLIMITED_ACCESS') return null;
  if (machine.status !== 'in_use' || !machine.started_at || !machine.duration_minutes) return null;
  const total = Math.round(Number(machine.duration_minutes) * 60);
  const used = Math.floor((now - new Date(machine.started_at).getTime()) / 1000);
  return total - used;
}
function fmtSecs(secs) {
  secs = Math.max(0, Math.floor(Number(secs) || 0));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
function isExeControlled(machine) {
  // Só o PC roda o .exe (trava sozinho). Consoles/cockpit/VR NÃO — precisam de alerta manual.
  return machine.device_type === 'pc' || !machine.device_type;
}
function isMachineOnline(machine) {
  return Number(machine?.is_online || 0) === 1;
}

export default function Machines() {
  const [machines, setMachines] = useState([]);
  const [clients, setClients] = useState([]);
  const [zones, setZones] = useState([]);
  const [packages, setPackages] = useState([]);
  const [deviceCategories, setDeviceCategories] = useState([]);
  const [modal, setModal] = useState(null); // {type, machine}
  const [form, setForm] = useState({ client_id: '', package_id: '', payment_method: 'cash' });
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [dragMachineId, setDragMachineId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [controlStatus, setControlStatus] = useState(null); // {machineId,command,state,message}
  const [dismissed, setDismissed] = useState({});   // ids de sessões esgotadas já dispensadas no popup
  const audioRef = useRef(null);
  const alertRef = useRef({});                        // id -> { level, lastBeep }
  const dragStateRef = useRef(null);

  const beep = (freq = 880, dur = 0.18, vol = 0.2) => {
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = 'sine'; o.frequency.value = freq; g.gain.value = vol;
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + dur);
    } catch (e) { /* áudio bloqueado até 1º clique */ }
  };

  const load = async () => {
    const [m, c, z, p, dc] = await Promise.all([
      machineService.getAll(),
      clientService.getAll(),
      zoneService.getAll(),
      packageService.getAll(),
      categoryService.getAll('device'),
    ]);
    setMachines(m.data);
    setClients(c.data);
    setZones(z.data);
    setPackages(p.data);
    setDeviceCategories(dc.data);
  };

  useEffect(() => {
    load();
    const refresh = setInterval(load, 30000);
    return () => clearInterval(refresh);
  }, []);

  // tick por segundo (contadores regressivos)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // som de alerta: entra em aviso (1 bip) e, ao esgotar, alarme repetido a cada 4s
  useEffect(() => {
    machines.forEach(m => {
      const r = remainingSecs(m, now);
      if (r === null) return;
      const prev = alertRef.current[m.id] || { level: 'ok', lastBeep: 0 };
      const level = r <= 0 ? 'expired' : (r <= WARN_SECS ? 'warn' : 'ok');
      if (level === 'warn' && prev.level === 'ok') {
        beep(880, 0.15, 0.18);
        alertRef.current[m.id] = { level, lastBeep: now };
      } else if (level === 'expired' && (prev.level !== 'expired' || now - prev.lastBeep > 4000)) {
        beep(494, 0.28, 0.3); setTimeout(() => beep(392, 0.32, 0.3), 320);   // alarme "sonar"
        alertRef.current[m.id] = { level, lastBeep: now };
      } else if (level !== prev.level) {
        alertRef.current[m.id] = { level, lastBeep: prev.lastBeep };
      }
    });
  }, [now, machines]);

  useSocket((event, data) => {
    if (event === 'machine:screenshot') {
      const machine = machines.find(m => Number(m.id) === Number(data.machine_id));
      setScreenshot({ ...data, machine });
      setControlStatus(prev => Number(prev?.machineId) === Number(data.machine_id) && prev?.command === 'screenshot'
        ? { ...prev, state: 'success', message: 'Tela capturada com sucesso.' }
        : prev);
      return;
    }
    if (event === 'machine:control:result') {
      const messages = {
        logoff: data.success ? 'Logoff confirmado pela máquina.' : 'Não foi possível fazer o logoff.',
        shutdown: data.success ? 'Desligamento confirmado pela máquina.' : 'Não foi possível desligar o PC.',
        screenshot: data.success ? 'Captura confirmada pela máquina.' : 'Não foi possível capturar a tela.',
      };
      setControlStatus({
        machineId: Number(data.machine_id),
        command: data.command,
        state: data.success ? 'success' : 'error',
        message: data.message || messages[data.command] || (data.success ? 'Comando confirmado.' : 'Falha ao executar o comando.'),
      });
      return;
    }
    if (['session:started', 'session:updated', 'session:ended', 'machine:online', 'machine:offline'].includes(event)) load();
  });

  const sendControl = async (machine, command) => {
    const labels = { screenshot: 'capturar a tela', logoff: 'fazer logoff', shutdown: 'desligar o PC' };
    if (command !== 'screenshot' && !window.confirm(`Deseja ${labels[command]} ${machine.name}?`)) return;
    const pendingMessages = { screenshot: 'Solicitando captura...', logoff: 'Fazendo logoff...', shutdown: 'Desligando...' };
    setControlStatus({ machineId: Number(machine.id), command, state: 'pending', message: pendingMessages[command] });
    try {
      await machineService.control(machine.id, command);
      if (command === 'screenshot') setScreenshot({ loading: true, machine });
      setControlStatus(prev => Number(prev?.machineId) === Number(machine.id) && prev?.command === command && prev?.state === 'pending'
        ? { ...prev, state: 'waiting', message: `${pendingMessages[command]} Aguardando confirmação da máquina.` }
        : prev);
      setTimeout(() => setControlStatus(prev =>
        Number(prev?.machineId) === Number(machine.id) && prev?.command === command && ['pending','waiting'].includes(prev.state)
          ? { ...prev, state: 'error', message: 'Comando enviado, mas a máquina não confirmou a execução.' }
          : prev
      ), 15000);
    } catch (err) {
      setControlStatus({ machineId: Number(machine.id), command, state: 'error', message: err.response?.data?.error || err.message });
    }
  };

  const addLoggedCredit = async () => {
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return;
    setLoading(true);
    try {
      await clientService.addBalance(modal.machine.client_id, { amount, payment_method: form.payment_method });
      setModal(null); await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  };

  const sellLoggedPackage = async () => {
    if (!form.package_id) return;
    const pkg = packages.find(item => String(item.id) === String(form.package_id));
    if (!pkg) return;
    setLoading(true);
    try {
      await packageService.sell(form.package_id, { client_id: modal.machine.client_id, payment_splits: buildPaymentSplits(pkg.price, form.cash_amount, form.other_method || 'pix') });
      setModal(null); await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  };

  const sellCounterCredit = async () => {
    const amount = Number(String(form.amount || '').replace(',', '.'));
    if (!amount || !form.package_id || !(form.machine_ids || []).length) return;
    setLoading(true);
    try {
      await packageService.counterCredit({
        amount,
        package_id: Number(form.package_id),
        machine_ids: form.machine_ids.map(Number),
        payment_method: form.payment_method || 'cash',
      });
      setModal(null);
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setLoading(false); }
  };

  // sessões esgotadas (tempo zerou) que ainda precisam de ação do operador
  const expired = machines.filter(m => {
    const r = remainingSecs(m, now);
    return r !== null && r <= 0 && !dismissed[m.session_id];
  });

  const handleQuickAddClient = async (clientData) => {
    const res = await clientService.create(clientData);
    const newId = res.data?.id;
    await load();
    setForm(prev => ({
      ...prev,
      client_id: newId,
      payment_method: prev.payment_method === 'balance' ? 'cash' : prev.payment_method
    }));
    return newId;
  };

  const handleStart = async () => {
    setLoading(true);
    try {
      await sessionService.start({
        machine_id: modal.machine.id,
        client_id: form.client_id || null,
        package_id: form.package_id || null,
        payment_method: form.payment_method,
      });
      setModal(null);
      load();
    } catch (err) {
      alert('Erro ao iniciar sessão: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEnd = async (machine) => {
    if (!window.confirm(`Encerrar sessão da ${machine.name}?`)) return;
    try {
      await sessionService.end(machine.session_id, { payment_method: 'cash' });
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleNewZone = async () => {
    const name = window.prompt('Nome da nova zona:');
    if (!name) return;
    try {
      await zoneService.create({ name, sort_order: zones.length + 1 });
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditZone = async (zone) => {
    const name = window.prompt('Novo nome da zona:', zone.name);
    if (!name || name === zone.name) return;
    try {
      await zoneService.update(zone.id, { name, sort_order: zone.sort_order });
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteZone = async (zone) => {
    if (!window.confirm(`Excluir a zona "${zone.name}"? As máquinas dela ficam sem zona.`)) return;
    try {
      await zoneService.delete(zone.id);
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    }
  };

  const startDrag = (event, zone, items, machine) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(machine.id));
    dragStateRef.current = { id: machine.id, zoneId: zone?.id || null, original: machines, order: items.map(m => m.id) };
    setDragMachineId(machine.id);
    setDragOverId(machine.id);
  };

  const previewDrag = (zone, items, targetId) => {
    const draggedId = dragStateRef.current?.id;
    if (!draggedId || savingOrder || (zone?.id || null) !== dragStateRef.current?.zoneId) return;
    const from = items.findIndex(m => m.id === draggedId);
    const to = items.findIndex(m => m.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    setDragOverId(targetId);
    const next = [...items];
    const [dragged] = next.splice(from, 1);
    next.splice(to, 0, dragged);
    dragStateRef.current.order = next.map(m => m.id);
    setMachines(current => {
      let index = 0;
      return current.map(machine => {
        const sameZone = (machine.zone_id || null) === (zone?.id || null);
        return sameZone ? next[index++] : machine;
      });
    });
  };

  const handleDrop = async (event, zone) => {
    event.preventDefault();
    const state = dragStateRef.current;
    if (!state || !state.order?.length || savingOrder) return;
    const order = state.order;
    dragStateRef.current = null;
    setDragMachineId(null);
    setDragOverId(null);
    setSavingOrder(true);
    try {
      await machineService.reorder({ zone_id: zone?.id || null, machine_ids: order });
      await load();
    } catch (err) {
      setMachines(state.original);
      alert('Não foi possível salvar a nova ordem: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingOrder(false);
    }
  };

  const cancelDrag = () => {
    if (dragStateRef.current) setMachines(dragStateRef.current.original);
    dragStateRef.current = null;
    setDragMachineId(null);
    setDragOverId(null);
  };

  const elapsed = (started_at) => {
    const diff = Math.floor((Date.now() - new Date(started_at)) / 60000);
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  // Agrupa as máquinas por zona, na ordem das zonas + "Sem zona" no fim
  const grouped = [
    ...zones.map(z => ({
      zone: z,
      items: machines.filter(m => m.zone_id === z.id),
    })),
    { zone: null, items: machines.filter(m => !m.zone_id) },
  ].filter(g => g.zone !== null || g.items.length > 0);   // zonas sempre visíveis; "Sem zona" só se tiver máquina

  const inUse = machines.filter(m => m.session_id).length;
  const onlineWaiting = machines.filter(m => isMachineOnline(m) && !m.session_id).length;
  const offline = machines.filter(m => !isMachineOnline(m)).length;
  const activeControl = contextMenu && Number(controlStatus?.machineId) === Number(contextMenu.machine.id)
    ? controlStatus : null;
  const controlBusy = ['pending', 'waiting'].includes(activeControl?.state);
  const commandBusy = command => controlBusy && activeControl?.command === command;

  return (
    <div>
      <style>{`
        @keyframes arenaSonar { 0% { box-shadow: 0 0 0 0 rgba(245,158,11,0.5); } 70% { box-shadow: 0 0 0 14px rgba(245,158,11,0); } 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); } }
        @keyframes arenaSonarRed { 0% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); } 70% { box-shadow: 0 0 0 16px rgba(239,68,68,0); } 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); } }
        @keyframes arenaDropGlow { 0%,100% { box-shadow: 0 0 0 2px rgba(163,230,53,.35), 0 0 12px rgba(163,230,53,.18); } 50% { box-shadow: 0 0 0 3px rgba(163,230,53,.9), 0 0 28px rgba(163,230,53,.5); } }
        @keyframes arenaSpin { to { transform: rotate(360deg); } }
      `}</style>

      {/* POPUP: tempo esgotado (precisa de ação do operador) */}
      {expired.length > 0 && (
        <div style={styles.alertOverlay}>
          <div style={styles.alertBox}>
            <div style={styles.alertTitle}>⛔ Tempo esgotado</div>
            <div style={styles.alertSub}>
              {expired.some(m => !isExeControlled(m))
                ? 'Aparelhos sem PC (consoles/VR) não travam sozinhos — encerre aqui.'
                : 'Sessões com o tempo zerado.'}
            </div>
            {expired.map(m => (
              <div key={m.id} style={styles.alertRow}>
                <span style={{ fontSize: 20 }}>{deviceMeta(m.device_type).icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{m.name}
                    {!isExeControlled(m) && <span style={styles.manualTag}>manual</span>}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>
                    {m.client_nick ? '@' + m.client_nick : (m.client_name || 'Sem cadastro')}
                  </div>
                </div>
                <button style={styles.btnEnd} onClick={() => handleEnd(m)}>⏹ Encerrar</button>
                <button style={{ ...styles.btnSecondary, padding: '9px 12px' }}
                  onClick={() => setDismissed(d => ({ ...d, [m.session_id]: true }))} title="Silenciar">🔕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Mapa da Arena</h1>
          <p style={styles.sub}>
            {inUse} logados • {onlineWaiting} ligados aguardando login • {offline} desligados
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={styles.btnCounterCredit} onClick={() => {
            setModal({ type: 'counter-credit' });
            setForm({ amount: '', package_id: '', machine_ids: [], payment_method: 'cash' });
          }}>⚡ Adicionar crédito</button>
          <button style={styles.btnSecondary} onClick={handleNewZone}>+ Zona</button>
          <button style={styles.btnPrimary} onClick={() => setModal({ type: 'add' })}>
            + Máquina
          </button>
        </div>
      </div>

      {/* MAPA COMPACTO: todas as zonas compartilham a mesma grade para ocupar os vazios */}
      <div style={styles.arenaMap}>
        <div style={styles.zoneBar}>
          {grouped.map(({ zone, items }) => (
            <div key={zone ? zone.id : 'no-zone'} style={styles.zoneChip}>
              <span style={styles.zoneName}>{zone ? zone.name : 'Sem zona'}</span>
              <span style={styles.zoneCount}>{items.length}</span>
              {zone && <>
                <button style={styles.zoneBtn} title="Renomear zona" onClick={() => handleEditZone(zone)}>✏️</button>
                <button style={styles.zoneBtn} title="Excluir zona" onClick={() => handleDeleteZone(zone)}>🗑️</button>
              </>}
            </div>
          ))}
          <span style={styles.dragHint}>{savingOrder ? 'Salvando ordem…' : 'Arraste pelo botão ↕ para organizar dentro da zona'}</span>
        </div>
        <div style={styles.grid}>
          {grouped.flatMap(({ zone, items }) =>
            items.map(machine => (
              <div key={machine.id}
                onDragOver={e => { e.preventDefault(); previewDrag(zone, items, machine.id); }}
                onDragEnter={() => previewDrag(zone, items, machine.id)}
                onDrop={e => handleDrop(e, zone)}
                style={{
                  opacity: dragMachineId === machine.id ? 0.38 : 1,
                  transform: dragOverId === machine.id && dragMachineId !== machine.id ? 'translateX(7px) scale(.98)' : 'translateX(0) scale(1)',
                  borderRadius: 13,
                  animation: dragOverId === machine.id && dragMachineId !== machine.id ? 'arenaDropGlow .8s infinite' : 'none',
                  transition: 'transform .18s ease, opacity .18s ease',
                  position: 'relative',
                }}>
                <span style={styles.machineZoneBadge}>{zone ? zone.name : 'Sem zona'}</span>
                {dragOverId === machine.id && dragMachineId !== machine.id && <span style={styles.dropLabel}>SOLTAR AQUI</span>}
                <MachineCard
                  machine={machine}
                  elapsed={elapsed}
                  remaining={remainingSecs(machine, now)}
                  onDragStart={e => startDrag(e, zone, items, machine)}
                  onDragEnd={cancelDrag}
                  onStart={() => { setModal({ type: 'start', machine }); setForm({ client_id: '', package_id: '', payment_method: 'cash' }); }}
                  onEnd={() => handleEnd(machine)}
                  onRelease={() => machineService.update(machine.id, { ...machine, status: 'available' }).then(load)}
                  onEdit={() => setModal({ type: 'edit', machine })}
                  onQuickActions={() => { setControlStatus(null); setContextMenu({ machine }); }}
                />
              </div>
            ))
          )}
        </div>
      </div>

      {!machines.length && (
        <div style={styles.empty}>Nenhuma máquina cadastrada. Clique em “+ Máquina”.</div>
      )}

      {contextMenu && <div style={styles.overlay} onMouseDown={e => e.target === e.currentTarget && setContextMenu(null)}>
        <div style={{ ...styles.modal, width: 380, maxWidth: 'calc(100vw - 28px)' }}>
          <div style={styles.quickHead}>
            <div><span style={styles.quickBolt}>⚡</span><strong>{contextMenu.machine.name}</strong><small style={styles.quickStatus}>{contextMenu.machine.client_name || (isMachineOnline(contextMenu.machine) ? 'Ligado · aguardando jogador' : 'Desligado')}</small></div>
            <button style={styles.closeQuick} onClick={() => setContextMenu(null)}>×</button>
          </div>
          {activeControl && <div style={{...styles.controlFeedback,...(activeControl.state==='success'?styles.controlSuccess:activeControl.state==='error'?styles.controlError:styles.controlPending)}}>
            {controlBusy && <span style={styles.controlSpinner}/>}<strong>{activeControl.message}</strong>
          </div>}
          <div style={styles.quickList}>
            <button style={styles.quickItem} disabled={!contextMenu.machine.client_id} onClick={() => { setModal({ type: 'credit', machine: contextMenu.machine }); setForm({ amount: '', payment_method: 'cash' }); setContextMenu(null); }}>💰 <span style={styles.quickItemText}><strong>Colocar crédito</strong><small>Adicionar saldo ao jogador logado</small></span></button>
            <button style={styles.quickItem} disabled={!contextMenu.machine.client_id} onClick={() => { setModal({ type: 'package', machine: contextMenu.machine }); setForm({ package_id: '', cash_amount: '', other_method: 'pix' }); setContextMenu(null); }}>🎟️ <span style={styles.quickItemText}><strong>Inserir/comprar pacote</strong><small>Vender horas ao jogador logado</small></span></button>
            <button style={styles.quickItem} disabled={!isMachineOnline(contextMenu.machine)||controlBusy} onClick={() => sendControl(contextMenu.machine, 'screenshot')}>📸 <span style={styles.quickItemText}><strong>{commandBusy('screenshot')?'Capturando tela…':'Capturar tela'}</strong><small>{commandBusy('screenshot')?'Aguardando resposta do PC':'Solicitar uma imagem deste PC'}</small></span></button>
            <button style={styles.quickItem} disabled={!contextMenu.machine.client_id||controlBusy} onClick={() => sendControl(contextMenu.machine, 'logoff')}>🚪 <span style={styles.quickItemText}><strong>{commandBusy('logoff')?'Fazendo logoff…':'Logoff do jogador'}</strong><small>{commandBusy('logoff')?'Aguardando confirmação do PC':'Encerrar o acesso atual'}</small></span></button>
            <button style={{ ...styles.quickItem, color: '#fca5a5' }} disabled={!isMachineOnline(contextMenu.machine)||controlBusy} onClick={() => sendControl(contextMenu.machine, 'shutdown')}>⏻ <span style={styles.quickItemText}><strong>{commandBusy('shutdown')?'Desligando…':'Desligar PC'}</strong><small>{commandBusy('shutdown')?'Aguardando confirmação do PC':'Economizar energia'}</small></span></button>
          </div>
        </div>
      </div>}

      {screenshot && <div style={styles.overlay} onMouseDown={e => e.target === e.currentTarget && setScreenshot(null)}><div style={{ ...styles.modal, width: 900 }}><h2 style={styles.modalTitle}>Tela — {screenshot.machine?.name || 'PC'}</h2>{screenshot.loading ? <div style={styles.screenWaiting}>Solicitando captura ao PC…</div> : <img src={screenshot.image} alt="Captura do PC" style={styles.screenImage}/>}<div style={styles.modalActions}><button style={styles.btnSecondary} onClick={() => setScreenshot(null)}>Fechar</button></div></div></div>}

      {modal?.type === 'credit' && <div style={styles.overlay}><div style={styles.modal}><h2 style={styles.modalTitle}>Colocar crédito — {modal.machine.client_name}</h2><div style={styles.field}><label style={styles.label}>Valor em R$</label><input autoFocus type="number" min="1" style={styles.input} value={form.amount || ''} onChange={e => setForm({ ...form, amount: e.target.value })}/></div><PaymentField form={form} setForm={setForm}/><div style={styles.modalActions}><button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button><button style={styles.btnStart} disabled={loading || !form.amount} onClick={addLoggedCredit}>Adicionar crédito</button></div></div></div>}

      {modal?.type === 'package' && <div style={styles.overlay}><div style={styles.modal}><h2 style={styles.modalTitle}>Pacote — {modal.machine.client_name}</h2><div style={styles.field}><label style={styles.label}>Pacote de horas</label><select style={styles.input} value={form.package_id || ''} onChange={e => setForm({ ...form, package_id: e.target.value })}><option value="">Selecione</option>{packages.map(p => <option key={p.id} value={p.id}>{p.name} — {p.minutes}min · R$ {Number(p.price).toFixed(2)}</option>)}</select></div><div style={styles.field}><label style={styles.label}>Valor em dinheiro</label><input type="number" min="0" step="0.01" style={styles.input} value={form.cash_amount || ''} onChange={e => setForm({ ...form, cash_amount: e.target.value })}/></div><div style={styles.field}><label style={styles.label}>Restante em</label><select style={styles.input} value={form.other_method || 'pix'} onChange={e => setForm({ ...form, other_method: e.target.value })}><option value="pix">PIX</option><option value="card_debit">Cartão débito</option><option value="card_credit">Cartão crédito</option></select></div><div style={styles.modalActions}><button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button><button style={styles.btnStart} disabled={loading || !form.package_id} onClick={sellLoggedPackage}>Vender pacote</button></div></div></div>}

      {modal?.type === 'counter-credit' && (() => {
        const amount = Number(String(form.amount || '').replace(',', '.')) || 0;
        const availablePackages = packages.filter(p => Number(p.price) <= amount);
        const selectedPackage = packages.find(p => String(p.id) === String(form.package_id));
        const eligibleMachines = machines.filter(m => {
          const isLoggedPc = (m.device_type === 'pc' || !m.device_type) && m.client_id;
          const isRegularPlayer = Number(m.client_allow_multi_login || 0) !== 1 && m.session_notes !== 'UNLIMITED_ACCESS';
          const categoryMatches = !selectedPackage?.category_id || Number(m.package_category_id) === Number(selectedPackage.category_id);
          return isLoggedPc && isRegularPlayer && categoryMatches;
        });
        const selectedIds = form.machine_ids || [];
        const toggleMachine = id => setForm(current => ({
          ...current,
          machine_ids: (current.machine_ids || []).includes(id)
            ? current.machine_ids.filter(item => item !== id)
            : [...(current.machine_ids || []), id],
        }));
        const total = selectedPackage ? Number(selectedPackage.price) * selectedIds.length : 0;
        return <div style={styles.overlay} onMouseDown={e => e.target === e.currentTarget && setModal(null)}>
          <div style={{ ...styles.modal, width: 620 }}>
            <h2 style={styles.modalTitle}>⚡ Adicionar crédito aos jogadores</h2>
            <p style={styles.modalIntro}>Venda no balcão: escolha o valor, o pacote e os PCs com jogadores logados.</p>
            <div style={styles.twoFields}>
              <div style={{ ...styles.field, flex: 1 }}>
                <label style={styles.label}>Valor disponível por jogador</label>
                <input autoFocus inputMode="decimal" style={styles.input} placeholder="Ex.: 35,00" value={form.amount || ''}
                  onChange={e => setForm({ ...form, amount: e.target.value, package_id: '', machine_ids: [] })}/>
              </div>
              <CounterPaymentField form={form} setForm={setForm}/>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Pacote disponível</label>
              <select style={styles.input} value={form.package_id || ''} disabled={!amount}
                onChange={e => setForm({ ...form, package_id: e.target.value, machine_ids: [] })}>
                <option value="">{amount ? 'Selecione o pacote' : 'Informe primeiro o valor'}</option>
                {availablePackages.map(p => <option key={p.id} value={p.id}>{p.name} — {p.minutes} min · R$ {Number(p.price).toFixed(2)}</option>)}
              </select>
              {amount > 0 && !availablePackages.length && <small style={styles.fieldWarning}>Nenhum pacote disponível para esse valor.</small>}
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Jogadores logados nos PCs</label>
              {!selectedPackage ? <div style={styles.selectionEmpty}>Selecione um pacote para liberar os PCs compatíveis.</div>
                : eligibleMachines.length ? <div style={styles.playerGrid}>{eligibleMachines.map(m => {
                  const selected = selectedIds.includes(m.id);
                  return <button type="button" key={m.id} style={{ ...styles.playerPick, ...(selected ? styles.playerPickOn : {}) }} onClick={() => toggleMachine(m.id)}>
                    <span style={styles.playerCheck}>{selected ? '✓' : ''}</span>
                    <span><strong>{m.name}</strong><small>{m.client_nick ? '@' + m.client_nick : m.client_name}</small></span>
                  </button>;
                })}</div>
                : <div style={styles.selectionEmpty}>Nenhum jogador comum está logado em um PC compatível. Suporte e acesso livre não aparecem aqui.</div>}
            </div>
            {selectedPackage && selectedIds.length > 0 && <div style={styles.saleSummary}>
              <span>{selectedIds.length} jogador{selectedIds.length > 1 ? 'es' : ''} · {selectedPackage.minutes} min para cada</span>
              <strong>Total: R$ {total.toFixed(2)}</strong>
            </div>}
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button>
              <button style={{ ...styles.btnPrimary, minWidth: 190 }} disabled={loading || !selectedPackage || !selectedIds.length} onClick={sellCounterCredit}>
                {loading ? 'Confirmando…' : 'Confirmar e creditar'}
              </button>
            </div>
          </div>
        </div>;
      })()}

      {/* MODAL START SESSION */}
      {modal?.type === 'start' && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Iniciar Sessão — {modal.machine.name}</h2>

            {(() => {
              const pkg = packages.find(p => String(p.id) === String(form.package_id));
              const minutes = pkg ? pkg.minutes : 0;
              const value = pkg ? Number(pkg.price) : 0;
              const selectedClient = clients.find(c => String(c.id) === String(form.client_id));
              const clientBalance = selectedClient ? Number(selectedClient.balance || 0) : 0;
              return (
                <>
                  <div style={styles.field}>
                    <label style={styles.label}>Cliente (opcional)</label>
                    <ClientSelectSearch
                      clients={clients}
                      selectedClientId={form.client_id}
                      onSelectClient={(newClientId) => {
                        const newClient = clients.find(c => String(c.id) === String(newClientId));
                        const newBalance = newClient ? Number(newClient.balance || 0) : 0;
                        setForm({
                          ...form,
                          client_id: newClientId,
                          payment_method: newBalance > 0 ? 'balance' : (form.payment_method === 'balance' ? 'cash' : form.payment_method)
                        });
                      }}
                      onQuickAddClient={handleQuickAddClient}
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.label}>Pacote</label>
                    <select style={styles.input} value={form.package_id}
                      onChange={e => setForm({ ...form, package_id: e.target.value })}>
                      <option value="">Selecione um pacote</option>
                      {packages.map(p => (
                        <option key={p.id} value={p.id}>{p.name} — {p.minutes}min · R$ {Number(p.price).toFixed(2)}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ ...styles.field, flex: 1 }}>
                      <label style={styles.label}>Forma de pagamento</label>
                      <select style={styles.input} value={form.payment_method}
                        onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                        {clientBalance > 0 && <option value="balance">Comprar com Saldo</option>}
                        <option value="cash">Dinheiro</option>
                        <option value="pix">PIX</option>
                        <option value="card_debit">Cartão débito</option>
                        <option value="card_credit">Cartão crédito</option>
                      </select>
                    </div>
                    <div style={{ ...styles.field, flex: 1 }}>
                      <label style={styles.label}>Tempo · Valor</label>
                      <div style={{ ...styles.input, display: 'flex', flexDirection: 'column', gap: 2, height: 'auto' }}>
                        <span style={{ color: '#e5e7eb', fontSize: 13 }}>{minutes} min</span>
                        <span style={{ color: '#22c55e', fontSize: 15, fontWeight: 700 }}>R$ {value.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div style={styles.modalActions}>
                    <button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button>
                    <button style={styles.btnStart} onClick={handleStart} disabled={loading || !form.package_id}>
                      {loading ? 'Iniciando...' : '▶ Iniciar e cobrar'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* MODAL ADD MACHINE */}
      {modal?.type === 'add' && (
        <AddMachineModal zones={zones} deviceCategories={deviceCategories} onClose={() => setModal(null)} onSave={load} />
      )}

      {/* MODAL EDIT MACHINE */}
      {modal?.type === 'edit' && (
        <EditMachineModal machine={modal.machine} zones={zones} deviceCategories={deviceCategories} onClose={() => setModal(null)} onSave={load} />
      )}
    </div>
  );
}

function PaymentField({ form, setForm }) {
  return <div style={styles.field}><label style={styles.label}>Forma de pagamento</label><select style={styles.input} value={form.payment_method || 'cash'} onChange={e => setForm({ ...form, payment_method: e.target.value })}><option value="cash">Dinheiro</option><option value="pix">PIX</option><option value="card_debit">Cartão débito</option><option value="card_credit">Cartão crédito</option></select></div>;
}

function CounterPaymentField({ form, setForm }) {
  return <div style={{ ...styles.field, flex: 1 }}><label style={styles.label}>Pagamento no balcão</label><select style={styles.input} value={form.payment_method || 'cash'} onChange={e => setForm({ ...form, payment_method: e.target.value })}><option value="cash">Dinheiro</option><option value="card_debit">Cartão débito</option><option value="card_credit">Cartão crédito</option></select></div>;
}

function MachineCard({ machine, elapsed, remaining, onStart, onEnd, onRelease, onEdit, onQuickActions, onDragStart, onDragEnd }) {
  const on = Boolean(machine.session_id);
  const unlimited = machine.session_notes === 'UNLIMITED_ACCESS';
  const connected = isMachineOnline(machine);
  const warn = remaining !== null && remaining <= WARN_SECS && remaining > CRIT_SECS;
  const crit = remaining !== null && remaining <= CRIT_SECS && remaining > 0;
  const expired = remaining !== null && remaining <= 0;
  let pulse = null;
  if (on && expired) pulse = { animation: 'arenaSonarRed 1s infinite', borderColor: '#ef4444', borderWidth: 2, borderStyle: 'solid' };
  else if (on && crit) pulse = { animation: 'arenaSonarRed 1.4s infinite', borderColor: '#ef4444', borderWidth: 2, borderStyle: 'solid' };
  else if (on && warn) pulse = { animation: 'arenaSonar 1.8s infinite', borderColor: '#f59e0b', borderWidth: 2, borderStyle: 'solid' };
  const timerColor = (expired || crit) ? '#ef4444' : warn ? '#f59e0b' : '#22c55e';

  return (
    <div style={{ ...styles.machineCard, ...operationalCardStyle(connected, on), ...(pulse || {}) }}>
      {machine.is_customer_visible===0&&<span style={styles.hiddenBadge}>OFF · OCULTO DO CLIENTE</span>}
      <button draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
        style={styles.dragHandle} title="Arrastar para mudar a posição">↕</button>
      {onEdit && (
        <button style={styles.editBtn} title="Editar dispositivo" onClick={onEdit}>⚙️</button>
      )}
      <button style={styles.quickBtn} title="Ações rápidas" aria-label={`Ações rápidas de ${machine.name}`} onClick={onQuickActions}>
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M13.3 2 5 13h6l-.8 9L19 10h-6.1l.4-8Z" fill="currentColor"/></svg>
      </button>
      <div style={{ ...styles.devIcon, ...(connected ? styles.devIconOn : styles.devIconOff) }}>
        {deviceMeta(machine.device_type).icon}
      </div>
      <div style={styles.machineName}>{machine.name}</div>

      {on ? (
        <div style={styles.sessionInfo}>
          <div style={styles.nick}>👤 {machine.client_nick ? '@' + machine.client_nick : (machine.client_name || 'Sem cadastro')}</div>
          {unlimited ? (
            <div style={{ ...styles.timer, color: '#a3e635' }}>∞ ACESSO LIVRE</div>
          ) : remaining === null ? (
            <div style={{ ...styles.timer, color: '#60a5fa' }}>⏱️ {elapsed(machine.started_at)}</div>
          ) : expired ? (
            <div style={{ ...styles.timer, color: '#ef4444', fontSize: 14 }}>⛔ ESGOTADO</div>
          ) : (
            <>
              <div style={{ ...styles.timer, color: timerColor }}>⏳ {fmtSecs(remaining)}</div>
              {Number(machine.queued_minutes || 0) > 0 && (
                <div style={{ color: '#a3e635', fontSize: 12, fontWeight: 800 }}>
                  + {fmtSecs(Math.round(Number(machine.queued_minutes) * 60))} na fila
                </div>
              )}
              <div style={styles.progTrack}>
                <div style={{ ...styles.progFill, width: `${Math.max(0, Math.min(100, (remaining / Math.max(1, Math.round(Number(machine.duration_minutes) * 60))) * 100))}%`, background: timerColor }} />
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ ...styles.freeLabel, color: connected ? '#f87171' : '#6b7280' }}>
          {connected ? '● LIGADO · AGUARDANDO LOGIN' : '● DESLIGADO / SEM COMUNICAÇÃO'}
        </div>
      )}

      {machine.booking_id && (
        <div style={machine.booking_active ? styles.bookingActive : styles.bookingSoon}>
          {machine.booking_active ? '🔒 RESERVADO' : '📅 RESERVA'}
          {' · '}{machine.booking_client_nick || machine.booking_client_name || 'cliente'}
          {' · até '}{new Date(machine.booking_ends_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      <div style={styles.machineActions}>
        {machine.status === 'available' && (
          <button style={styles.btnStart} onClick={onStart}>▶ Iniciar</button>
        )}
        {on && (
          <button style={{ ...styles.btnEnd, ...(expired ? { background: '#b91c1c' } : crit ? { background: '#dc2626' } : warn ? { background: '#d97706' } : {}) }} onClick={onEnd}>
            {expired ? '⛔ Encerrar' : '⏹ Encerrar'}
          </button>
        )}
        {machine.status === 'maintenance' && (
          <button style={styles.btnSecondary} onClick={onRelease}>✅ Liberar</button>
        )}
      </div>
    </div>
  );
}

function AddMachineModal({ zones, deviceCategories, onClose, onSave }) {
  const [form, setForm] = useState({ name: '', description: '', zone_id: '', sort_order: 0, device_type: 'pc', package_category_id: '', is_customer_visible: true });
  const handleSave = async () => {
    await machineService.create({ ...form, zone_id: form.zone_id || null });
    onSave();
    onClose();
  };
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2 style={styles.modalTitle}>Nova Máquina</h2>
        {['name', 'description'].map(f => (
          <div key={f} style={styles.field}>
            <label style={styles.label}>{f === 'name' ? 'Nome *' : 'Descrição'}</label>
            <input style={styles.input} value={form[f]} onChange={e => setForm({ ...form, [f]: e.target.value })} />
          </div>
        ))}
        <div style={styles.field}>
          <label style={styles.label}>Tipo de dispositivo</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {Object.entries(DEVICES).map(([k, d]) => (
              <button key={k} type="button"
                onClick={() => setForm({ ...form, device_type: k })}
                style={{ ...styles.devPick, ...(form.device_type === k ? styles.devPickOn : {}) }}>
                <span style={{ fontSize: 22 }}>{d.icon}</span>
                <span style={{ fontSize: 11 }}>{d.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Categoria dos valores</label>
          <select style={styles.input} value={form.package_category_id} onChange={e => setForm({ ...form, package_category_id: e.target.value })}>
            <option value="">Sem categoria de valores</option>
            {deviceCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <small style={styles.sub}>Define quais pacotes aparecem no botão “VALORES” deste aparelho.</small>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Zona</label>
            <select style={styles.input} value={form.zone_id} onChange={e => setForm({ ...form, zone_id: e.target.value })}>
              <option value="">Sem zona</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </div>
          <div style={{ ...styles.field, width: 90 }}>
            <label style={styles.label}>Ordem</label>
            <input type="number" style={styles.input} value={form.sort_order}
              onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
          </div>
        </div>
        <div style={styles.modalActions}>
          <button style={styles.btnSecondary} onClick={onClose}>Cancelar</button>
          <button style={styles.btnStart} onClick={handleSave}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

const DEVICES = {
  pc:        { icon: '🖥️', label: 'PC' },
  ps5:       { icon: '🎮', label: 'PS5' },
  cockpit:   { icon: '🏎️', label: 'Cockpit' },
  metaquest: { icon: '🥽', label: 'Meta Quest' },
};
function deviceMeta(t) { return DEVICES[t] || DEVICES.pc; }
function EditMachineModal({ machine, zones, deviceCategories, onClose, onSave }) {
  const [tvCode, setTvCode] = useState(machine.tv_pair_code || '');
  const [tvMessage, setTvMessage] = useState('');
  const [tvLoading, setTvLoading] = useState(false);
  const [form, setForm] = useState({
    name: machine.name || '', description: machine.description || '',
    zone_id: machine.zone_id || '',
    sort_order: machine.sort_order || 0, device_type: machine.device_type || 'pc',
    package_category_id: machine.package_category_id || '',
    status: machine.status || 'available',
    is_customer_visible: machine.is_customer_visible!==0,
    lockdown_level: machine.lockdown_level || 'none',
  });
  const handleSave = async () => {
    await machineService.update(machine.id, { ...form, zone_id: form.zone_id || null });
    onSave(); onClose();
  };
  const handleDelete = async () => {
    if (!window.confirm(`Excluir "${machine.name}"?`)) return;
    await machineService.delete(machine.id);
    onSave(); onClose();
  };
  const pairTv = async () => {
    setTvLoading(true); setTvMessage('');
    try {
      const { data } = await machineService.pairTv(machine.id, tvCode);
      setTvMessage(`✓ TV vinculada a ${data.machine_name}`); onSave();
    } catch (err) { setTvMessage(err.response?.data?.error || err.message); }
    finally { setTvLoading(false); }
  };
  const unpairTv = async () => {
    if (!window.confirm(`Desvincular a TV de ${machine.name}?`)) return;
    await machineService.unpairTv(machine.id); setTvCode(''); setTvMessage('TV desvinculada'); onSave();
  };
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.editTitleRow}><h2 style={styles.modalTitle}>Editar — {machine.name}</h2><button type="button" onClick={()=>setForm({...form,is_customer_visible:!form.is_customer_visible})} style={{...styles.visibilityToggle,...(form.is_customer_visible?styles.visibilityOn:styles.visibilityOff)}}><span style={styles.visibilityDot}/>{form.is_customer_visible?'ON':'OFF'}</button></div>
        <div style={{...styles.visibilityHelp,...(form.is_customer_visible?{}:{borderColor:'#7f1d1d',color:'#fca5a5'})}}>{form.is_customer_visible?'Visível no mapa do cliente e disponível para agendamentos.':'Oculto da área do cliente e de todos os agendamentos.'}</div>
        <div style={styles.field}>
          <label style={styles.label}>Nome *</label>
          <input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Proteção do Windows</label>
          <select style={styles.input} value={form.lockdown_level} onChange={e=>setForm({...form,lockdown_level:e.target.value})}>
            <option value="none">Nenhum</option>
            <option value="partial">Bloqueio parcial</option>
            <option value="total">Kiosk</option>
          </select>
          <small style={styles.sub}>{form.lockdown_level==='none'?'Janela pode ser recolhida para a bandeja.':form.lockdown_level==='partial'?'Alt+Tab permitido; Arena só encerra com senha administrativa.':'Alt+Tab permitido entre Arena, jogos e aplicativos; Arena só encerra com senha administrativa.'}</small>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Categoria dos valores</label>
          <select style={styles.input} value={form.package_category_id} onChange={e => setForm({ ...form, package_category_id: e.target.value })}>
            <option value="">Sem categoria de valores</option>
            {deviceCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <small style={styles.sub}>Os pacotes e o saldo de tempo desta categoria são exclusivos para os aparelhos vinculados a ela.</small>
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Tipo de dispositivo</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {Object.entries(DEVICES).map(([k, d]) => (
              <button key={k} type="button" onClick={() => setForm({ ...form, device_type: k })}
                style={{ ...styles.devPick, ...(form.device_type === k ? styles.devPickOn : {}) }}>
                <span style={{ fontSize: 22 }}>{d.icon}</span>
                <span style={{ fontSize: 11 }}>{d.label}</span>
              </button>
            ))}
          </div>
        </div>
        {form.device_type !== 'pc' && <div style={styles.tvPairBox}>
          <div style={styles.tvPairTitle}>📺 TV Samsung</div>
          {Boolean(machine.tv_linked) ? <>
            <div style={styles.tvLinked}>● VINCULADA · {machine.tv_model || 'Samsung TV'}</div>
            <small style={styles.sub}>{machine.tv_duid ? `DUID: ${machine.tv_duid}` : `Código: ${machine.tv_pair_code}`}</small>
            <button type="button" style={{...styles.btnSecondary,marginTop:10,padding:'7px 10px'}} onClick={unpairTv}>Desvincular TV</button>
          </> : <>
            <label style={styles.label}>Código mostrado no aplicativo da TV</label>
            <div style={{display:'flex',gap:8}}>
              <input style={{...styles.input,textTransform:'uppercase'}} placeholder="TV-123456" value={tvCode} onChange={e=>setTvCode(e.target.value.toUpperCase())}/>
              <button type="button" style={{...styles.btnStart,width:120}} disabled={tvLoading||!tvCode} onClick={pairTv}>{tvLoading?'Vinculando…':'Vincular'}</button>
            </div>
          </>}
          {tvMessage && <div style={{marginTop:8,color:tvMessage.startsWith('✓')?'#a3e635':'#fca5a5',fontSize:12}}>{tvMessage}</div>}
        </div>}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>Zona</label>
            <select style={styles.input} value={form.zone_id} onChange={e => setForm({ ...form, zone_id: e.target.value })}>
              <option value="">Sem zona</option>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </div>
        </div>
        <div style={styles.modalActions}>
          <button style={{ ...styles.btnSecondary, color: '#f87171', borderColor: '#7f1d1d' }} onClick={handleDelete}>Excluir</button>
          <button style={styles.btnSecondary} onClick={onClose}>Cancelar</button>
          <button style={styles.btnStart} onClick={handleSave}>Salvar</button>
        </div>
      </div>
    </div>
  );
}

function statusLabel(s) { return { available: 'Livre', in_use: 'Em uso', blocked: 'Bloqueada', maintenance: 'Manutenção' }[s] || s; }
// Livre = "apagado" (escuro); Em uso = verde destacado; manutenção/bloqueada = âmbar/vermelho
function operationalCardStyle(connected, logged) {
  if (logged) return { background: '#0c1f15', border: '2px solid #22c55e', boxShadow: '0 0 0 1px rgba(34,197,94,0.25), 0 0 22px rgba(34,197,94,0.18)' };
  if (connected) return { background: '#220d10', border: '2px solid #ef4444', boxShadow: '0 0 18px rgba(239,68,68,.14)' };
  return { background: '#0e0e16', border: '1px solid #20202e', opacity: .72 };
}

function cardStyle(s) {
  return {
    available:   { background: '#0e0e16', border: '1px solid #20202e', opacity: 0.9 },
    in_use:      { background: '#0c1f15', border: '2px solid #22c55e', boxShadow: '0 0 0 1px rgba(34,197,94,0.25), 0 0 22px rgba(34,197,94,0.18)' },
    maintenance: { background: '#1a1305', border: '1px solid #f59e0b' },
    blocked:     { background: '#1a0808', border: '1px solid #ef4444', opacity: 0.85 },
  }[s] || {};
}

const styles = {
  tvPairBox:{padding:14,margin:'4px 0 14px',background:'#101820',border:'1px solid #24435b',borderRadius:10},tvPairTitle:{color:'#fff',fontSize:14,fontWeight:800,marginBottom:10},tvLinked:{color:'#a3e635',fontSize:12,fontWeight:900,marginBottom:5},
  editTitleRow:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12},visibilityToggle:{display:'flex',alignItems:'center',gap:7,borderRadius:20,padding:'7px 11px',fontSize:11,fontWeight:900,cursor:'pointer'},visibilityOn:{background:'#1a2e05',border:'1px solid var(--accent)',color:'var(--accent)'},visibilityOff:{background:'#2a1014',border:'1px solid #ef4444',color:'#f87171'},visibilityDot:{width:8,height:8,borderRadius:'50%',background:'currentColor',boxShadow:'0 0 8px currentColor'},visibilityHelp:{padding:'9px 11px',border:'1px solid #365314',background:'#111a0c',borderRadius:8,color:'#bef264',fontSize:11,marginBottom:14},hiddenBadge:{position:'absolute',top:8,left:34,zIndex:3,padding:'3px 7px',borderRadius:10,background:'#450a0a',border:'1px solid #991b1b',color:'#fca5a5',fontSize:8,fontWeight:900,letterSpacing:.4},
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  title: { color: '#fff', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: '#6b7280', fontSize: 14, margin: '4px 0 0' },
  arenaMap: { marginBottom: 24, border: '2px dashed #2f2f45', borderRadius: 16, padding: '14px 18px 20px', background: 'rgba(255,255,255,0.012)' },
  zoneBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  zoneChip: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 28, padding: '3px 7px 3px 10px', background: '#15151f', border: '1px solid #303044', borderRadius: 999 },
  zoneHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  zoneName: { color: 'var(--accent)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .7 },
  zoneCount: { background: '#262636', color: '#9ca3af', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 20 },
  dragHint: { color: '#6b7280', fontSize: 11, marginRight: 'auto' },
  zoneBtn: { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, padding: '1px 2px', opacity: 0.7 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 },
  machineZoneBadge: { position: 'absolute', top: 7, left: '50%', transform: 'translateX(-50%)', zIndex: 4, maxWidth: '52%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '2px 7px', borderRadius: 999, background: '#20202d', border: '1px solid #343448', color: '#8b8b9b', fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .4, pointerEvents: 'none' },
  empty: { color: '#6b7280', textAlign: 'center', padding: 40 },
  machineCard: { position: 'relative', background: '#14141f', border: '1px solid #262636', borderRadius: 12, padding: '18px 14px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 },
  editBtn: { position: 'absolute', top: 6, right: 6, background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, opacity: 0.55, padding: 2 },
  dragHandle: { position: 'absolute', top: 5, left: 7, background: '#232334', border: '1px solid #35354a', borderRadius: 6, color: '#9ca3af', cursor: 'grab', fontSize: 16, lineHeight: 1, padding: '3px 7px' },
  dropLabel: { position: 'absolute', zIndex: 5, top: -10, left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: '#0a0a0f', borderRadius: 999, padding: '3px 9px', fontSize: 9, fontWeight: 900, whiteSpace: 'nowrap', pointerEvents: 'none' },
  devIcon: { fontSize: 46, lineHeight: 1 },
  devIconOn: { filter: 'none', opacity: 1 },
  devIconOff: { filter: 'grayscale(1)', opacity: 0.4 },
  machineName: { color: '#fff', fontWeight: 700, fontSize: 15 },
  sessionInfo: { display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' },
  nick: { color: '#e5e7eb', fontSize: 13, fontWeight: 600 },
  timer: { color: '#22c55e', fontSize: 18, fontWeight: 800, letterSpacing: 1 },
  progTrack: { width: '100%', height: 5, background: '#1c1c28', borderRadius: 999, overflow: 'hidden', marginTop: 6 },
  progFill: { height: '100%', borderRadius: 999, transition: 'width 1s linear, background .3s' },
  freeLabel: { color: '#6b7280', fontSize: 12, fontWeight: 600 },
  // Reserva em curso: e ela que faz o watchdog encerrar a sessao e manter o
  // equipamento livre. Sem o aviso aqui o operador nao descobre o motivo.
  bookingActive: { marginTop: 8, padding: '6px 10px', background: '#2a1a05', border: '1px solid #f59e0b', borderRadius: 8, color: '#fbbf24', fontSize: 11, fontWeight: 800 },
  bookingSoon: { marginTop: 8, padding: '6px 10px', background: '#0c1a24', border: '1px solid #38bdf8', borderRadius: 8, color: '#7dd3fc', fontSize: 11, fontWeight: 700 },
  machineActions: { marginTop: 4, width: '100%' },
  btnPrimary: { padding: '10px 18px', background: 'var(--accent)', color: '#0a0a0f', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnCounterCredit: { padding: '10px 18px', background: 'var(--accent)', color: '#0a0a0f', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 800, boxShadow: '0 0 18px rgba(163,230,53,.18)' },
  btnSecondary: { padding: '10px 18px', background: 'transparent', border: '1px solid #374151', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 14 },
  btnStart: { width: '100%', padding: '9px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnEnd: { width: '100%', padding: '9px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#14141f', border: '1px solid #262636', borderRadius: 16, padding: 28, width: 440, maxWidth: '90vw' },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 20px' },
  modalIntro: { color: '#9ca3af', fontSize: 13, lineHeight: 1.45, margin: '-10px 0 18px' },
  twoFields: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  field: { marginBottom: 14 },
  label: { display: 'block', color: '#9ca3af', fontSize: 13, fontWeight: 500, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box' },
  fieldWarning: { display: 'block', color: '#fbbf24', fontSize: 11, marginTop: 6 },
  selectionEmpty: { padding: 14, border: '1px dashed #36364b', borderRadius: 10, color: '#7f8494', fontSize: 12, lineHeight: 1.4 },
  playerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, maxHeight: 230, overflowY: 'auto' },
  playerPick: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 11px', background: '#1b1b29', border: '1px solid #343448', borderRadius: 10, color: '#fff', textAlign: 'left', cursor: 'pointer' },
  playerPickOn: { border: '1px solid var(--accent)', background: '#19220f', boxShadow: 'inset 0 0 0 1px rgba(163,230,53,.18)' },
  playerCheck: { width: 20, height: 20, flex: '0 0 20px', borderRadius: 5, display: 'grid', placeItems: 'center', border: '1px solid #55556c', background: '#11111a', color: 'var(--accent)', fontWeight: 900 },
  saleSummary: { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 14px', borderRadius: 10, background: '#18220d', border: '1px solid #3f6212', color: '#d9f99d', fontSize: 13 },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 },
  devPick: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 4px', background: '#1c1c2b', border: '1px solid #34344e', borderRadius: 10, color: '#cbd5e1', cursor: 'pointer' },
  devPickOn: { border: '2px solid var(--accent)', background: '#22223a', color: '#fff' },
  alertOverlay: { position: 'fixed', top: 16, right: 16, zIndex: 100, maxWidth: 420 },
  alertBox: { background: '#1a0808', border: '2px solid #ef4444', borderRadius: 14, padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' },
  alertTitle: { color: '#fca5a5', fontSize: 18, fontWeight: 800, marginBottom: 4 },
  alertSub: { color: '#f59e0b', fontSize: 12, marginBottom: 12 },
  alertRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #3f1414' },
  manualTag: { background: '#f59e0b', color: '#0a0a0f', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 6, marginLeft: 6 },
  quickBtn: { position: 'absolute', top: 9, right: 37, width: 22, height: 22, padding: 0, border: 0, background: 'transparent', color: '#a3e635', cursor: 'pointer', display: 'grid', placeItems: 'center', zIndex: 3 },
  quickHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, color: '#fff', marginBottom: 14 },
  quickBolt: { color: '#a3e635', marginRight: 9, fontSize: 20 },
  quickStatus: { display: 'block', margin: '4px 0 0 31px', color: '#8b8b9d', fontSize: 12 },
  closeQuick: { width: 32, height: 32, borderRadius: 9, border: '1px solid #343445', background: '#181824', color: '#fff', cursor: 'pointer', fontSize: 20 },
  quickList: { display: 'grid', gap: 8 },
  quickItem: { width: '100%', minHeight: 54, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 12, background: '#181824', color: '#e5e7eb', border: '1px solid #303043', borderRadius: 10, textAlign: 'left', cursor: 'pointer', fontSize: 18 },
  quickItemText: { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 },
  controlFeedback: { display:'flex',alignItems:'center',gap:9,padding:'10px 12px',marginBottom:10,borderRadius:9,fontSize:12,border:'1px solid' },
  controlPending: { color:'#fde68a',background:'#2a2109',borderColor:'#a16207' },
  controlSuccess: { color:'#bbf7d0',background:'#092619',borderColor:'#15803d' },
  controlError: { color:'#fecaca',background:'#2b0d12',borderColor:'#b91c1c' },
  controlSpinner: { width:13,height:13,border:'2px solid currentColor',borderRightColor:'transparent',borderRadius:'50%',animation:'arenaSpin .8s linear infinite',flexShrink:0 },
  screenWaiting: { minHeight: 320, display: 'grid', placeItems: 'center', color: '#9ca3af', background: '#08080d', borderRadius: 10 },
  screenImage: { display: 'block', width: '100%', maxHeight: '70vh', objectFit: 'contain', background: '#08080d', borderRadius: 10 },
};

function ClientSelectSearch({ clients, selectedClientId, onSelectClient, onQuickAddClient }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [newClientForm, setNewClientForm] = useState({ name: '', phone: '', nickname: '', birth_date: '' });
  const [adding, setAdding] = useState(false);
  const dropdownRef = useRef(null);

  const selectedClient = (Array.isArray(clients) ? clients : []).find(c => String(c.id) === String(selectedClientId));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredClients = (Array.isArray(clients) ? clients : []).filter(c => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    const name = String(c?.name || '').toLowerCase();
    const nick = String(c?.nickname || '').toLowerCase();
    const phone = String(c?.phone || '').toLowerCase();
    const cpf = String(c?.cpf || '').toLowerCase();
    return name.includes(term) || nick.includes(term) || phone.includes(term) || cpf.includes(term);
  });

  const handleCreateNewClient = async (e) => {
    e.preventDefault();
    if (!newClientForm.name.trim()) return alert('Informe o nome do cliente');
    const cleanDate = (newClientForm.birth_date || '').replace(/\D/g, '');
    if (cleanDate.length !== 8 || !isValidDate(newClientForm.birth_date)) {
      return alert('Informe a data de aniversário completa no formato DD/MM/AAAA (ex: 15/08/1995)');
    }
    setAdding(true);
    try {
      await onQuickAddClient({
        ...newClientForm,
        birth_date: dateToIso(newClientForm.birth_date)
      });
      setShowQuickAdd(false);
      setNewClientForm({ name: '', phone: '', nickname: '', birth_date: '' });
      setSearchTerm('');
      setIsOpen(false);
    } catch (err) {
      alert('Erro ao cadastrar cliente: ' + (err.response?.data?.error || err.message));
    } finally {
      setAdding(false);
    }
  };

  if (selectedClient) {
    const balance = Number(selectedClient.balance || 0);
    return (
      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justify: 'space-between',
          background: '#262636',
          border: '1px solid #3b82f6',
          borderRadius: 8,
          padding: '8px 12px',
          color: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>👤</span>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 14 }}>{selectedClient.name}</div>
              {selectedClient.phone && <div style={{ color: '#9ca3af', fontSize: 11 }}>📱 {selectedClient.phone}</div>}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSelectClient('')}
            style={{
              background: 'transparent',
              border: 0,
              color: '#ef4444',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: 16,
              padding: '2px 6px',
            }}
            title="Remover / Trocar cliente"
          >
            ✕
          </button>
        </div>
        {balance > 0 && (
          <div style={{ color: '#22c55e', fontSize: 12, marginTop: 4, fontWeight: 600 }}>
            💰 Saldo em conta: R$ {balance.toFixed(2)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder="🔍 Buscar por nome, WhatsApp ou apelido..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          style={{
            width: '100%',
            padding: '10px 14px',
            background: '#262636',
            border: '1px solid #374151',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14,
            boxSizing: 'border-box',
          }}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 0,
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            ✕
          </button>
        )}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: '#1a1a26',
          border: '1px solid #374151',
          borderRadius: 8,
          maxHeight: 220,
          overflowY: 'auto',
          zIndex: 1100,
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}>
          {filteredClients.length > 0 ? (
            filteredClients.slice(0, 15).map(c => (
              <div
                key={c.id}
                onClick={() => {
                  onSelectClient(c.id);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                style={{
                  padding: '9px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #262636',
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#262636'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                  <div style={{ color: '#9ca3af', fontSize: 11 }}>
                    {c.nickname ? `@${c.nickname}` : ''} {c.phone ? `• ${c.phone}` : ''}
                  </div>
                </div>
                {Number(c.balance || 0) > 0 && (
                  <span style={{ color: '#22c55e', fontSize: 11, fontWeight: 'bold' }}>
                    R$ {Number(c.balance).toFixed(2)}
                  </span>
                )}
              </div>
            ))
          ) : (
            <div style={{ padding: 12, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
              Nenhum cliente encontrado para "{searchTerm}"
            </div>
          )}

          {/* Atalho para Adicionar Cliente */}
          <button
            type="button"
            onClick={() => {
              setNewClientForm({ name: searchTerm, phone: '', nickname: '' });
              setShowQuickAdd(true);
              setIsOpen(false);
            }}
            style={{
              width: '100%',
              padding: 10,
              background: '#2563eb',
              color: '#fff',
              border: 0,
              fontWeight: 'bold',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justify: 'center',
              gap: 6,
            }}
          >
            ➕ Adicionar Cliente {searchTerm ? `"${searchTerm}"` : ''}
          </button>
        </div>
      )}

      {/* MODAL / FORM DE CADASTRO RÁPIDO DE CLIENTE */}
      {showQuickAdd && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justify: 'center',
          zIndex: 1200,
        }} onClick={() => setShowQuickAdd(false)}>
          <div style={{
            background: '#14141f',
            border: '1px solid #374151',
            borderRadius: 12,
            padding: 20,
            width: 360,
            maxWidth: '90vw',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 16, fontWeight: 'bold' }}>👤 Novo Cliente Rápido</h3>
              <button type="button" style={{ background: 'none', border: 0, color: '#9ca3af', fontSize: 18, cursor: 'pointer' }} onClick={() => setShowQuickAdd(false)}>×</button>
            </div>

            <form onSubmit={handleCreateNewClient} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Nome Completo *</label>
                <input
                  type="text"
                  required
                  placeholder="Nome do cliente"
                  value={newClientForm.name}
                  onChange={e => setNewClientForm({ ...newClientForm, name: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                  autoFocus
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Data de Aniversário *</label>
                <input
                  type="text"
                  required
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                  maxLength={10}
                  value={newClientForm.birth_date}
                  onChange={e => setNewClientForm({ ...newClientForm, birth_date: maskDate(e.target.value) })}
                  style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>WhatsApp / Telefone</label>
                <input
                  type="text"
                  placeholder="(00) 00000-0000"
                  value={newClientForm.phone}
                  onChange={e => setNewClientForm({ ...newClientForm, phone: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Apelido / Nickname</label>
                <input
                  type="text"
                  placeholder="ex: player1"
                  value={newClientForm.nickname}
                  onChange={e => setNewClientForm({ ...newClientForm, nickname: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowQuickAdd(false)}
                  style={{ padding: '8px 14px', background: '#374151', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={adding}
                  style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}
                >
                  {adding ? 'Salvando...' : 'Salvar e Selecionar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
